/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import {useTheme} from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';

export interface UseMedia {
  isPhone: boolean;
  isTablet: boolean;
  isDesktop: boolean;
}

/**
 * @hook useMedia
 *
 * Custom hook using the mui media query for the theme that returns an object
 * that can be destructured to view when breakpoints are reached on screen
 * resizing.
 *
 * ```typescript
 *
 *   import {useMedia} from '@ion/dls/src/hooks/useMedia';
 *
 *   const SomeComponent = () => {
 *     const {isPhone, isTablet, isDesktop} = useMedia();
 *     if (isPhone) {
 *       ...
 *     }
 *   }
 *
 * ```
 */
export function useMedia(): UseMedia {
  const theme     = useTheme();
  const isPhone   = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet  = useMediaQuery(theme.breakpoints.down('md'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  return {isPhone, isTablet, isDesktop};
}

/**
 * @hook useIsPhone
 *
 * Custom hook using the mui media query for the theme that returns a boolean
 * indicating if the application is being viewed on a phone (true), or not,
 * (false).
 *
 * ```typescript
 *
 *   import {useIsPhone} from '@ion/dls/src/hooks/useMedia';
 *
 *   const SomeComponent = () => {
 *     const isPhone = useIsPhone();
 *     if (isPhone) {
 *       ...
 *     }
 *   }
 *
 * ```
 */
export function useIsPhone(): boolean {
  const theme    = useTheme();
  const isPhone  = useMediaQuery(theme.breakpoints.down('sm'));
  return isPhone;
}

/**
 * @hook useIsTablet
 *
 * Custom hook using the mui media query for the theme that returns a boolean
 * indicating if the application is being viewed on a table (true), or not,
 * (false).
 *
 * ```typescript
 *
 *   import {useIsTablet} from '@ion/dls/src/hooks/';
 *
 *   const SomeComponent = () => {
 *     const isTablet = useIsTablet();
 *     if (isTablet) {
 *       ...
 *     }
 *   }
 *
 * ```
 */
export function useIsTablet(): boolean {
  const theme    = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  return isTablet;
}

/**
 * @hook useIsDesktop
 *
 * Custom hook using the mui media query for the theme that returns a boolean
 * indicating if the application is being viewed on a desktop (true), or not,
 * (false).
 *
 * ```typescript
 *
 *   import {useIsDesktop} from '@ion/dls/src/hooks/useMedia';
 *
 *   const SomeComponent = () => {
 *     const isDesktop = useIsDesktop();
 *     if (isDesktop) {
 *       ...
 *     }
 *   }
 *
 * ```
 */
export function useIsDesktop(): boolean {
  const theme     = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  return isDesktop;
}

export default useMedia;
